/**
 * AVA-475 — the one place that knows how big a viewer unit is.
 *
 * THE UNIT CONTRACT (the whole reason this file exists)
 *
 * Everything in Verification state is VIEWER units — whatever the resolved Forge
 * model calls a coordinate. Millimetre scalars are produced by the transform's
 * `mmPerViewerUnit` at the point of display or comparison. Site metres are
 * produced by `viewerToSite` at the five display/API leaves enumerated below.
 * NO SITE-METRE VALUE IS EVER WRITTEN TO THE STORE.
 *
 * The transform is built ONCE from the resolved model by
 * `RESOLVE_VERIFIED_GEOMETRY` and read from state — no consumer resolves it from
 * the live viewer.
 *
 * THE FIVE `viewerToSite` CONSUMERS — this list is the contract, not a comment:
 *
 *   1. `modules/verification.module.js`  GET_REPORT_MODEL — the report clone
 *   2. `modules/verification.module.js`  GET_AUTO_SCAN — compared against
 *      panorama camera positions, which the API states in site metres
 *   3. `components/results/PointResultCard.vue` — the position cells, which
 *      render live under a metres caption
 *   4. `components/workspace/OffsetCheckView.vue` — the raw and adjusted
 *      position columns of the dual-offset panel, likewise live. Its OFFSET
 *      column is a difference and takes `viewerVectorToSite`, which is why that
 *      converter is deliberately not on this list.
 *   5. `components/workspace/VerificationWizard.vue` — the read-only "≈ site"
 *      preview under the design and manual entry fields. The fields themselves
 *      stay VIEWER units and nothing converts INBOUND; the preview exists so the
 *      surveyor can see the model-frame number they are typing and the site
 *      metres consumer 3 will print for it at the same moment.
 *
 * A SIXTH CONSUMER REQUIRES EDITING THIS HEADER. That is the point of writing
 * them down: the previous attempt converted at CAPTURE instead, and the store
 * then fed site metres back into viewer APIs at sinks nobody had counted —
 * marker projection, the crop box, the frame guard, the axis lock. Converting
 * outward at named leaves makes the conversions one-way and countable; a
 * conversion at capture makes every viewer sink a place an inverse was owed.
 *
 * WHY EVERY ENTRY POINT HERE TAKES A MODEL, NEVER A VIEWER
 *
 * A viewer is not a unit system — a MODEL is, and this feature's route loads TWO
 * of them. `verifiedBBox.js:26-33` records the measurement: alongside the NWD
 * design model there is a 1-node `{3D}` model, `viewer.model` points at whichever
 * is current, and there is a real window during load where that is the empty one.
 * An empty model reports `getUnitScale() === 0`, so any resolver that walks the
 * viewer's models has to GUESS which one to believe — and guessing right on the
 * metre facilities while guessing wrong on Merchant is precisely the failure this
 * file exists to end. The choice is therefore made once, by the code that already
 * proved which model owns the geometry (`resolveVerifiedBBox`), and everything
 * downstream consumes the frozen transform it produced.
 *
 * WHY A VIEWER UNIT IS NOT A METRE
 *
 * The feature was built and live-verified only against facilities whose Forge
 * model is authored in METRES, and encoded that as a constant rather than a
 * question: `pointMath.METRES_TO_MM = 1000` assumed a viewer coordinate WAS a
 * metre. On `Master Test Site / Merchant` the model is authored in MILLIMETRES
 * (`getUnitString() === 'mm'`, `getUnitScale() === 0.001`, facility bounding box
 * 106695 x 100440 x 142452), so every magnitude read out of the viewer was a
 * thousand times larger than the code believed:
 *
 *   - an 850 mm post reported its bounding box as 850 METRES
 *   - a real 3 mm deviation became `3 * 1000 = 3000 mm` against a +/-3 mm
 *     tolerance, so every check FAILED
 *
 * Nothing about that looks wrong on screen, which is the dangerous part — the
 * numbers are confident, internally consistent, and completely false.
 *
 * A FACILITY IS NOT ONE UNIT SYSTEM. Measured live on Merchant: the Forge model
 * is millimetres, while the API's level elevations (`29..33.299`) and panorama
 * `forgeCameraPosition` values (`x 5.5, y 15.4, z 31`) are METRES. So there is
 * no single facility-wide switch to flip — model space and API space must be
 * reconciled wherever they meet, which is what consumer 2 above is doing.
 */
import { warn as debugWarn } from '@/utils/debugLogger';

/**
 * A coordinate as the viewer states it. Metres on HPC/EDF, millimetres on
 * Merchant. This is what Verification state holds, everywhere, always.
 * @typedef {{x:number, y:number, z:number}} ViewerPoint
 */

/**
 * A real-world site coordinate in METRES — what a report and the API talk in.
 * Structurally identical to a ViewerPoint, which is exactly why the distinction
 * has to be written down.
 * @typedef {{x:number, y:number, z:number}} SitePoint
 */

/**
 * A scalar in millimetres. Deviations, tolerances and sizes are all this.
 * @typedef {number} Millimetres
 */

/**
 * Everything one resolved model says about units, frozen. `siteScale` is metres
 * per viewer unit; `siteOffset` is in VIEWER units, applied before scaling.
 * @typedef {{mmPerViewerUnit: Millimetres, siteScale: number, siteOffset: ViewerPoint}} UnitTransform
 */

/**
 * Build the feature's unit transform from ONE explicitly chosen model.
 *
 * PASS THE MODEL THAT OWNS THE GEOMETRY — in Wave 1 that is
 * `resolveVerifiedBBox(...).model`, the only model empirically proven to yield
 * fragments for the verified tag's dbIds. See the header for why nothing here
 * will pick a model on the caller's behalf.
 *
 * RETURNS NULL RATHER THAN GUESSING. An absent or still-loading model has no
 * units to report, and the historical default of 1 is what made a millimetre
 * facility read as metres for the whole life of this feature. A null transform
 * makes its consumers fail VISIBLE — deltas render as '—' and the point reads
 * pending — which is recoverable. A guessed transform produces a confident,
 * internally consistent, thousand-fold-wrong conformance document, which is not.
 *
 * @param {object|null} model A loaded Forge model.
 * @returns {UnitTransform|null} Frozen; null when the model cannot be trusted.
 */
export function buildUnitTransform(model) {
  const siteScale = metresPerModelUnit(model);
  if (siteScale === null) return null;

  return Object.freeze({
    mmPerViewerUnit: 1000 * siteScale,
    siteScale,
    siteOffset: Object.freeze(modelGlobalOffset(model)),
  });
}

/**
 * Convert a viewer-space point to SITE metres.
 *
 * TWO CORRECTIONS, NOT ONE. Unit scale alone is not enough. Forge loads a model
 * with its `globalOffset` subtracted so coordinates stay near the origin and
 * keep float precision, so a viewer coordinate is displaced from the real site
 * coordinate by that offset. Measured live on `Master Test Site / Merchant`
 * (offset -9527.9, 26904.7, -21474 model units):
 *
 *   scale only          25.17, -24.15, 61.71  -> outside every deck, nearest
 *                                                360 setup 40.85 m away
 *   scale + offset      15.64,   2.75, 40.24  -> on the Weather Deck, nearest
 *                                                360 setup 6.98 m away
 *
 * Both answers look like coordinates. Only the second is where the support is.
 *
 * WHY MAGNITUDES DO NOT NEED THIS: a deviation is `asBuilt - design`, and a
 * constant offset cancels in a subtraction, so `mmPerViewerUnit` alone is the
 * correct and complete conversion for a difference. The offset matters only for
 * POSITIONS that leave the app.
 *
 * Returns a NEW object, and the result is DISCARDED after use. Callers pass
 * points that are in the store; transforming one in place would corrupt a
 * recorded reading, and keeping the result would put site metres back in state —
 * the exact failure this contract exists to prevent. Only the five consumers
 * named in the file header may call this.
 *
 * @param {ViewerPoint|null} point
 * @param {UnitTransform|null} transform From `buildUnitTransform`.
 * @returns {SitePoint|null} null in, null out — INCLUDING a null transform,
 *   which must surface as a missing value rather than an unconverted one.
 */
export function viewerToSite(point, transform) {
  if (!isFinitePoint(point)) return null;
  if (!transform || !(transform.siteScale > 0) || !isFinitePoint(transform.siteOffset)) return null;
  const { siteScale, siteOffset } = transform;
  return {
    x: (point.x + siteOffset.x) * siteScale,
    y: (point.y + siteOffset.y) * siteScale,
    z: (point.z + siteOffset.z) * siteScale,
  };
}

/**
 * Convert a viewer-space VECTOR — a difference, not a position — to metres.
 *
 * SCALE ONLY, AND THAT IS THE WHOLE POINT. `viewerToSite` applies the model's
 * `globalOffset` because a position is displaced by it. A vector is the
 * difference of two positions, so the constant offset cancels; applying it here
 * would add tens of thousands of metres to a five-millimetre correction.
 *
 * NOT a `viewerToSite` consumer, and it does not belong on that list: no site
 * FRAME is involved, only a magnitude, and magnitudes may be converted
 * anywhere — that is what `mmPerViewerUnit` does throughout the feature. The
 * enumerated list is about points that claim to say WHERE something is.
 *
 * Both callers are offset columns sitting beside a position column: the report
 * clone's offset sentence ("Design offset +0.0050 E"), and OffsetCheckView's
 * offset column and arrow geometry. Before this existed, offset and position
 * were both viewer units — wrong together, and therefore at least addable.
 * Making one of them truthful without the other is what makes them unaddable.
 *
 * @param {ViewerPoint|null} vector A difference in viewer units.
 * @param {UnitTransform|null} transform From `buildUnitTransform`.
 * @returns {{x:number,y:number,z:number}|null} Metres; null in, null out.
 */
export function viewerVectorToSite(vector, transform) {
  if (!isFinitePoint(vector)) return null;
  if (!transform || !(transform.siteScale > 0)) return null;
  const { siteScale } = transform;
  return {
    x: vector.x * siteScale,
    y: vector.y * siteScale,
    z: vector.z * siteScale,
  };
}

/**
 * Metres per unit of THIS model — 1 for a metre model, 0.001 for a millimetre
 * model, null when the model cannot say.
 *
 * The feature's name for the quantity; `readUnitScale` is how it is obtained.
 * They are separate so that everything this codebase knows about how Forge
 * reports units lives in exactly one function, and every caller here reads as
 * domain language rather than as a viewer-API detail.
 *
 * @param {object|null} model A loaded Forge model.
 * @returns {number|null} Metres per viewer unit; > 0, or null.
 */
export function metresPerModelUnit(model) {
  return readUnitScale(model);
}

/**
 * A model's unit scale, or null if it cannot be trusted.
 *
 * `getUnitScale()` is the authority. `getUnitString()` is only consulted when
 * the scale is missing, because a string comparison is a weaker signal — the
 * viewer reports several spellings ('mm', 'millimeter', 'millimeters').
 *
 * @param {object|null} model A loaded Forge model.
 * @returns {number|null} Metres per viewer unit; > 0, or null.
 */
export function readUnitScale(model) {
  if (!model) return null;

  if (typeof model.getUnitScale === 'function') {
    try {
      const scale = model.getUnitScale();
      // An empty model reports 0 — and the `{3D}` model on this very route is
      // one. Taking it would collapse every coordinate onto the origin and read
      // as "the support is perfectly on design".
      if (Number.isFinite(scale) && scale > 0) return scale;
    } catch {
      // fall through to the string form
    }
  }

  if (typeof model.getUnitString === 'function') {
    try {
      const unit = String(model.getUnitString() || '').toLowerCase();
      if (unit.startsWith('mm') || unit.startsWith('millim')) return 0.001;
      if (unit.startsWith('cm') || unit.startsWith('centim')) return 0.01;
      if (unit === 'm' || unit.startsWith('met')) return 1;
    } catch {
      // fall through
    }
  }

  return null;
}

/**
 * A model's `globalOffset`, in VIEWER units (it is applied before scaling).
 * Zero when the model does not declare one, which is the common case and what
 * every metre facility this feature was built against reported.
 *
 * @param {object|null} model A loaded Forge model.
 * @returns {ViewerPoint} Never null — an unresolvable offset is zero.
 */
export function modelGlobalOffset(model) {
  try {
    const off = model?.getData?.()?.globalOffset;
    if (isFinitePoint(off)) return { x: off.x, y: off.y, z: off.z };
  } catch {
    // A model without loaded data has no offset to give. Not exceptional.
  }
  return { x: 0, y: 0, z: 0 };
}

/**
 * How far outside a reference box a point may sit and still be believable.
 *
 * A design point legitimately sits just outside its support's crop box — the
 * surveyor typed one, or an offset moved it. A point in the WRONG SPACE misses
 * by three orders of magnitude. Ten box-radii is comfortably above the first and
 * far below the second, so the warning cannot cry wolf on real work.
 */
const PLAUSIBLE_RADIUS_FACTOR = 10;

/**
 * The smallest `globalOffset` worth using as a reference, in site metres, and
 * how far below it a point has to fall before it stops being believable.
 *
 * On an offset facility a viewer coordinate is `site / scale - offset`, so it
 * sits NEAR `-offset` — tens of thousands of units from the viewer origin on
 * Merchant. A leaked site-metre value sits within tens of units of it. Two
 * orders of magnitude between them is a wide, unambiguous gap.
 */
const MIN_REFERENCE_OFFSET_M = 1;
const DWARFED_BY_OFFSET_RATIO = 100;

/**
 * DEV-ONLY sanity warning: does this point look like it is in viewer space?
 *
 * The one mechanism that catches a space mix-up at the moment of the mistake.
 * `ViewerPoint` and `SitePoint` are structurally identical — same three finite
 * numbers — so nothing in plain JS distinguishes them, and the previous attempt
 * shipped three call sites that had silently swapped one for the other. What
 * DOES distinguish them is magnitude: a site-metre point on a millimetre model
 * is about a thousand times off, which is unmissable against known geometry.
 *
 * WARNS, NEVER THROWS. This is a diagnostic on a conformance tool, not a gate: a
 * false positive that halted a capture would be worse than the bug it guards.
 * It is also SILENT when it has nothing to judge against — a warning with no
 * evidence behind it teaches the reader to ignore warnings.
 *
 * PREFER A BOX. Pass the support's crop box when the call site has one (marker
 * projection, slice bounds and the frame guard all do): it is ~1000 units across,
 * and a leaked value misses it by tens of thousands. The transform is the weaker
 * reference and has a KNOWN BLIND SPOT — it can only judge by `siteOffset`, so on
 * a facility whose model declares no offset it stays silent however wrong the
 * point is. Metre facilities are exactly that case, and are also the case where
 * the two spaces coincide and nothing is wrong.
 *
 * @param {ViewerPoint|null} point The point about to be handed to a viewer API.
 * @param {{min:ViewerPoint, max:ViewerPoint}|UnitTransform|null} bboxOrTransform
 *   Reference geometry in viewer units, or the unit transform. Either shape.
 * @returns {void}
 */
export function assertViewerPoint(point, bboxOrTransform) {
  if (process.env.NODE_ENV === 'production') return;
  if (!isFinitePoint(point)) return;

  const box = normaliseBox(bboxOrTransform);
  if (box) {
    warnIfFarFromBox(point, box);
    return;
  }

  const transform = bboxOrTransform;
  if (transform && transform.siteScale > 0 && isFinitePoint(transform.siteOffset)) {
    warnIfDwarfedByOffset(point, transform);
  }
}

function warnIfFarFromBox(point, box) {
  const centre = boxCentre(box);
  const radius = boxRadius(box);
  if (!(radius > 0)) return;

  const distance = distanceBetween(point, centre);
  if (distance <= radius * PLAUSIBLE_RADIUS_FACTOR) return;

  debugWarn('[verification] point is not plausibly in viewer space', {
    point,
    reference: 'crop box',
    referenceCentre: centre,
    referenceRadius: radius,
    distance,
    likelyCause: 'a site-metre value reaching a viewer API — see modelUnits.js',
  });
}

function warnIfDwarfedByOffset(point, transform) {
  const offsetMagnitude = magnitudeOf(transform.siteOffset);
  // No usable offset means no reference — the blind spot named in the docblock.
  if (offsetMagnitude * transform.siteScale <= MIN_REFERENCE_OFFSET_M) return;

  const expectedAtLeast = offsetMagnitude / DWARFED_BY_OFFSET_RATIO;
  if (magnitudeOf(point) >= expectedAtLeast) return;

  debugWarn('[verification] point is not plausibly in viewer space', {
    point,
    reference: 'globalOffset',
    looksLikeSiteMetres: true,
    offsetMagnitude,
    expectedAtLeast,
    likelyCause: 'a site-metre value reaching a viewer API — see modelUnits.js',
  });
}

/** Accepts a THREE.Box3 or the plain `{min, max}` shape the feature passes around. */
function normaliseBox(box) {
  if (!isFinitePoint(box?.min) || !isFinitePoint(box?.max)) return null;
  return { min: box.min, max: box.max };
}

function boxCentre(box) {
  return {
    x: (box.min.x + box.max.x) / 2,
    y: (box.min.y + box.max.y) / 2,
    z: (box.min.z + box.max.z) / 2,
  };
}

/** Half the box diagonal — the radius of the sphere that contains it. */
function boxRadius(box) {
  const dx = box.max.x - box.min.x;
  const dy = box.max.y - box.min.y;
  const dz = box.max.z - box.min.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz) / 2;
}

function magnitudeOf(p) {
  return Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
}

function distanceBetween(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function isFinitePoint(p) {
  return !!p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
}
